import React, { useContext, useState } from 'react'
import { Loading } from '@/components/Loading'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Dialog } from '@/components/ui/dialog'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '@/utils/api/useFetch'
import { sleep } from '@/utils/sleep'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { MultiTierNetworkDto } from 'api/Trading-Accounts/Types/MultiTierNetworkDto'
import MoreButton from 'components/MoreButton/MoreButton'
import { useGridState } from 'features/DataGrid'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { networkColumns } from './IntroducingBrokerMTNetworkColumns'
import { IntroducingBrokerMTNetworkSubAccountEditModal } from './IntroducingBrokerMTNetworkSubAccountEdit.modal'
import { UpdateIntroducingBrokerMultiTierSubAccountFormValues } from './IntroducingBrokerMTNetworkSubAccountEditForm'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

interface IntroducingBrokerMTNetworkPageProps {
  data: IntroducingBrokerDetailsDto
}

const IntroducingBrokerMTNetworkPage: React.FC<IntroducingBrokerMTNetworkPageProps> = (props) => {
  const { data } = props
  const [MTNetworkData, setMTNetworkData] = useState<MultiTierNetworkDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [editSubIBProfileModal, setEditSubIBProfileModal] = useState<MultiTierNetworkDto | null>(
    null
  )
  const setToast = useContext(ToastContext)
  const { gridState, gridActions } = useGridState()
  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    const resp = await partnershipsApiClient.getMultiTierNetwork(data.id, {
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    setMTNetworkData(resp.items)
    setTableData({ ...tableData, meta: resp })
    return resp
  }, [
    data.id,
    gridState.pageSize,
    gridState.pageIndex,
    tableData?.skipCount,
    gridState.take,
    gridState.skip,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const { isLoading } = useFetchOne(callback)

  const handleExpand = (event: GridExpandChangeEvent) => {
    setMTNetworkData(
      MTNetworkData.map((network) =>
        network.introducingBroker.id === event.dataItem.introducingBroker.id
          ? { ...network, expanded: event.value }
          : network
      )
    )
  }

  const onSubmit = async (
    introducingBrokerId: string,
    values: UpdateIntroducingBrokerMultiTierSubAccountFormValues
  ) => {
    await partnershipsApiClient.updateIntroducingBrokerSubAccounts(introducingBrokerId, values)
    await sleep(1000)
    setToast(successToast(`Sub-account updated.`))
    setEditSubIBProfileModal(null)
    forceRefresh()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize) {
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    }
    const newPageIndex = skip / take + 1

    gridActions.setGridFilters({ take, skip, pageIndex: newPageIndex })
  }

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  // if (gridStatusState.isLoading) return <Spinner />
  return (
    <React.Fragment>
      {editSubIBProfileModal && (
        <Dialog
          open={!!editSubIBProfileModal}
          onOpenChange={(open) => !open && setEditSubIBProfileModal(null)}
        >
          <IntroducingBrokerMTNetworkSubAccountEditModal
            introducingBroker={data}
            onSubmit={onSubmit}
            data={editSubIBProfileModal}
            onClose={() => setEditSubIBProfileModal(null)}
          />
        </Dialog>
      )}
      <IntroducingBrokerNavigation data={data} isEdit={false} tab={IntroducingBrokerTab.NETWORK} />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Loading isLoading={isLoading}>
          <TableWithExport
            data={MTNetworkData}
            columns={networkColumns}
            hasPermissions={hasPermission(permissions, PermissionsEnum.CanViewMultiTierNetwork)}
          >
            <Grid
              {...gridState}
              detail={(gridDetailProps) => (
                <DetailComponent
                  dataIndex={gridDetailProps.dataIndex}
                  dataItem={gridDetailProps.dataItem}
                  onEdit={setEditSubIBProfileModal}
                />
              )}
              data={MTNetworkData}
              resizable
              expandField='expanded'
              sort={gridState.sort}
              total={gridState.total}
              take={gridState.take}
              skip={gridState.skip}
              onExpandChange={handleExpand}
              onPageChange={handleGridPageChange}
              pageable={true}
              style={{ zIndex: 0 }}
            >
              {networkColumns
                .filter((c) => c.show)
                .map((column, idx) => (
                  <Column key={idx + 2} {...column} />
                ))}
              <Column
                width={50}
                title=''
                cell={(gridCellProps: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          onClick: () => setEditSubIBProfileModal(gridCellProps.dataItem),
                        },
                      ]}
                    />
                  </td>
                )}
              />
            </Grid>
          </TableWithExport>
        </Loading>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default IntroducingBrokerMTNetworkPage

const DetailComponent: React.FC<
  GridDetailRowProps & { onEdit: (dataItem: MultiTierNetworkDto) => void }
> = (props) => {
  const aggregateClientData: MultiTierNetworkDto = props.dataItem
  const hasAggregateClientData = !!aggregateClientData
  if (hasAggregateClientData) {
    return (
      <Grid data={aggregateClientData.subAccounts} resizable>
        {networkColumns
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          width={50}
          title=''
          cell={(gridCellProps: GridCellProps) => {
            return (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      onClick: () => props.onEdit(gridCellProps.dataItem),
                    },
                  ]}
                />
              </td>
            )
          }}
        />
      </Grid>
    )
  }

  return (
    <div style={{ height: '50px', width: '100%' }}>
      <div style={{ position: 'absolute', width: '100%' }}>
        <div className='k-loading-image' />
      </div>
    </div>
  )
}
